/**
 * Where to put an outline's name so it sits INSIDE the shape: the pole of
 * inaccessibility (the point inside the polygon farthest from every edge),
 * after Mapbox's polylabel. A U's or an L's bounding-box centre can lie in
 * the gap, over the rooms there; the pole never does.
 *
 * Points are [x, y] pairs (numbers or numeric strings).
 */
const pts = (poly) => poly.map((p) => [Number(p[0]), Number(p[1])]);

/** Signed distance from (x, y) to the polygon's edges: positive inside, negative outside. */
export function signedDistance(x, y, poly) {
  let inside = false;
  let min = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const [ax, ay] = poly[i];
    const [bx, by] = poly[j];
    if ((ay > y) !== (by > y) && x < ((bx - ax) * (y - ay)) / (by - ay) + ax) inside = !inside;
    const dx = bx - ax;
    const dy = by - ay;
    const len = dx * dx + dy * dy;
    const t = len === 0 ? 0 : Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / len));
    const ex = x - (ax + t * dx);
    const ey = y - (ay + t * dy);
    min = Math.min(min, Math.sqrt(ex * ex + ey * ey));
  }
  return inside ? min : -min;
}

/** The centre of the polygon's area (shoelace), or null when it has none. */
function centroid(poly) {
  let a = 0; let cx = 0; let cy = 0;
  for (let i = 0; i < poly.length; i += 1) {
    const [x0, y0] = poly[i];
    const [x1, y1] = poly[(i + 1) % poly.length];
    const f = x0 * y1 - x1 * y0;
    a += f; cx += (x0 + x1) * f; cy += (y0 + y1) * f;
  }
  return a === 0 ? null : [cx / (3 * a), cy / (3 * a)];
}

/**
 * The pole of inaccessibility: { x, y, r } with r the distance to the nearest
 * edge. Precision is in the polygon's own units (fractions of the picture).
 * The centroid is the first guess, so a shape it suits keeps it.
 */
export function poleOf(polygon, precision = 0.001) {
  const poly = pts(polygon);
  const xs = poly.map((p) => p[0]);
  const ys = poly.map((p) => p[1]);
  const minX = Math.min(...xs); const maxX = Math.max(...xs);
  const minY = Math.min(...ys); const maxY = Math.max(...ys);
  const size = Math.min(maxX - minX, maxY - minY);
  if (!(size > 0)) return { x: (minX + maxX) / 2, y: (minY + maxY) / 2, r: 0 };
  const cell = (x, y, h) => {
    const d = signedDistance(x, y, poly);
    return { x, y, h, d, max: d + h * Math.SQRT2 };
  };
  const queue = [];
  const h0 = size / 2;
  for (let x = minX; x < maxX; x += size) for (let y = minY; y < maxY; y += size) queue.push(cell(x + h0, y + h0, h0));
  let best = cell((minX + maxX) / 2, (minY + maxY) / 2, 0);
  const c = centroid(poly);
  if (c) { const cc = cell(c[0], c[1], 0); if (cc.d > best.d) best = cc; }
  while (queue.length) {
    queue.sort((a, b) => b.max - a.max);
    const top = queue.shift();
    if (top.d > best.d) best = top;
    if (top.max - best.d > precision) {
      const h = top.h / 2;
      queue.push(cell(top.x - h, top.y - h, h), cell(top.x + h, top.y - h, h),
        cell(top.x - h, top.y + h, h), cell(top.x + h, top.y + h, h));
    }
  }
  return { x: best.x, y: best.y, r: Math.max(0, best.d) };
}

/** The stretch of the horizontal line at y that lies inside the polygon and holds x: [left, right] or null. */
function runAt(x, y, poly) {
  const cuts = [];
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const [ax, ay] = poly[i];
    const [bx, by] = poly[j];
    if ((ay > y) !== (by > y)) cuts.push(((bx - ax) * (y - ay)) / (by - ay) + ax);
  }
  cuts.sort((a, b) => a - b);
  for (let i = 0; i + 1 < cuts.length; i += 2) if (x >= cuts[i] && x <= cuts[i + 1]) return [cuts[i], cuts[i + 1]];
  return null;
}

/**
 * The box a non-rectangular outline's name goes in: centred on the pole,
 * as wide as the shape allows at the pole's height and a little above and
 * below it, so the whole box lies inside the shape. { x, y, w, h } as fractions.
 */
export function labelBoxOf(polygon) {
  const poly = pts(polygon);
  const { x, y, r } = poleOf(poly);
  const hh = r * 0.5;
  let left = -Infinity; let right = Infinity;
  [y - hh, y, y + hh].forEach((yy) => {
    const run = runAt(x, yy, poly);
    if (run) { left = Math.max(left, run[0]); right = Math.min(right, run[1]); }
  });
  if (!(right > left)) { left = x - r; right = x + r; }
  return { x: left, y: y - hh, w: right - left, h: hh * 2 };
}
